/**
 * Форма создания задачи на новом бэке.
 *
 * Живёт рядом с `useTaskCreateForm`, а не вместо него: вёрстка у модалок одна и
 * та же, а вот модель принципиально разная и свести их в один хук нельзя —
 * у legacy все участники это числовые id Bitrix, у v2 UUID-ы своих аккаунтов.
 *
 * Что из формы бэк принимает и что нет (см. `taskWriteRequest` в спеке):
 *  - принимает: title, description, responsibleId, deadline, timeEstimateSeconds,
 *    priority, taskControl;
 *  - команду (соисполнители, наблюдатели) в теле создания не принимает вовсе —
 *    она пишется маршрутом `/v2/tasks/{id}/assignees`, по человеку за запрос,
 *    уже после создания. Поэтому команда отправляется вторым шагом;
 *  - постановщика не принимает: автор берётся из сессии, выбрать другого нельзя;
 *  - чек-лист и связь с другой задачей — тоже отдельные маршруты
 *    (`/checklist`, `/relations`), поэтому они уходят такими же пост-шагами;
 *  - файлы, напоминание, привязку к CRM и родительскую задачу в теле создания
 *    не принимает — эти контролы в модалке остаются на своих местах, но
 *    заблокированы (см. NOT_PORTED_HINT).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useNativeUsersMap } from '@/entities/auth';
import {
  createChecklistItemV2,
  toggleChecklistItemV2,
} from '@/entities/task/api/task-checklist-v2';
import {
  linkTaskToDealV2,
  type CrmDealV2,
} from '@/entities/task/api/crm-links-v2';
import { IdBridge } from '@/shared/lib/idBridge';
import { parseHours } from '../desktop/redesign/checklistModel';
import {
  createTaskRelationV2,
  uploadTaskFileV2,
} from '@/entities/task/api/task-content-v2';
import {
  assignTaskUserV2,
  createTaskV2,
  type TaskV2AssigneeRole,
  type TaskV2Write,
} from '@/entities/task/api/tasks-v2';
import type { TaskV2Option } from '@/entities/task/ui/TaskV2Picker';
import { tasksV2Keys } from '@/entities/task/model/useTasksV2';
import { getApiErrorMessage } from '@/shared/api';
import {
  createTaskRecurrenceV2,
  saveTaskAsTemplateV2,
} from '@/entities/task/api/task-automation-v2';
import type { TaskTemplateV2 } from '@/entities/task/api/task-automation-v2';
import {
  buildRecurrencePayload,
  type RecurrencePeriodV2,
} from '@/entities/task/model/recurrence-v2';
import { useAuthStore } from '@/store/auth';

import type { FormChecklistValue } from '@/features/tasks/task-dialogs/fields/FormChecklistBlock';

import type { PersonItem } from '../types';
import { clampPriority, datetimeLocalToUTCISOString } from '../utils';

/** Пустой чек-лист формы — и начальное значение, и сброс при открытии. */
const EMPTY_CHECKLIST: FormChecklistValue = { checklists: [], items: [] };

/**
 * Переносит чек-лист формы в задачу.
 *
 * В v2 у задачи одно дерево пунктов, а форма умеет несколько именованных
 * списков. Поэтому каждый список становится корневым пунктом со своим
 * названием, а его пункты — детьми: так не теряется ни название списка, ни
 * вложенность, которую набрал пользователь.
 *
 * Пункты создаются по одному и по порядку: id родителя нужен раньше, чем
 * создаются его дети.
 */
async function pushChecklistV2(
  taskId: string,
  value: FormChecklistValue,
  uuidOf: (n: number) => string | undefined
) {
  for (const group of value.checklists) {
    const rootId = await createChecklistItemV2(taskId, {
      title: group.title || 'Чек-лист',
    });
    if (!rootId) {
      continue;
    }

    const groupItems = value.items.filter((i) => i.checklistId === group.id);
    const byParent = new Map<number | null, typeof groupItems>();
    for (const item of groupItems) {
      const key = item.parentId ?? null;
      byParent.set(key, [...(byParent.get(key) ?? []), item]);
    }

    const createChildren = async (
      parentLocalId: number | null,
      parentUuid: string
    ): Promise<void> => {
      for (const item of byParent.get(parentLocalId) ?? []) {
        const minutes = Math.round(parseHours(item.hours) * 60);
        const members = (item.members ?? [])
          .map(uuidOf)
          .filter((x): x is string => Boolean(x));
        const id = await createChecklistItemV2(taskId, {
          title: item.title,
          parentId: parentUuid,
          ...(minutes > 0 ? { planMinutes: minutes } : {}),
          ...(members.length ? { members } : {}),
        });
        if (!id) {
          continue;
        }
        // Отметить пункт при создании контракт не даёт — только переключением.
        if (item.isComplete) {
          await toggleChecklistItemV2(taskId, id);
        }
        await createChildren(item.id, id);
      }
    };

    await createChildren(null, rootId);
  }
}

/** Подсказка на заблокированных контролах — одна на все, чтобы не расходились. */
export const NOT_PORTED_HINT = 'Ещё не переехало на новый бэк';

/**
 * Ключи в стиле legacy-формы: модалку открывают из мест, которые собирают
 * предзаполнение под старый payload (`TaskCreateModal` их и перекладывает).
 */
export interface TaskV2CreateFormInitialData {
  /** `YYYY-MM-DDTHH:mm` — приходит из клика по слоту календаря. */
  DEADLINE?: string;
  /** Часы плана — оттуда же. */
  TIME_ESTIMATE_HOURS?: number;
  TITLE?: string;
  DESCRIPTION?: string;
}

interface UseTaskV2CreateFormParams {
  open: boolean;
  onClose: () => void;
  initialData?: TaskV2CreateFormInitialData;
}

/** Часы в «HH:MM» — как показывает поле «Время на выполнение». */
function hoursToHHMM(hours: number | undefined): string {
  if (!hours || !Number.isFinite(hours) || hours <= 0) {
    return '';
  }
  const total = Math.round(hours * 60);

  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(
    total % 60
  ).padStart(2, '0')}`;
}

/**
 * `datetime-local` → RFC3339 для Go. Пустое и нераспознанное дают `''`, а не
 * мусор: пустую строку в `deadline` бэк не принимает вовсе
 * (`parsing time "" as "T"`), такой ключ надо не отправлять.
 */
export function toRfc3339(datetimeLocal: string): string {
  return datetimeLocal ? datetimeLocalToUTCISOString(datetimeLocal) : '';
}

/** «HH:MM» → секунды. Мусор и пустое — undefined, поле необязательное. */
function hhmmToSeconds(value: string): number | undefined {
  const match = value.trim().match(/^(\d{1,3}):?(\d{2})?$/);
  if (!match) {
    return undefined;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  const seconds = hours * 3600 + minutes * 60;

  return seconds > 0 ? seconds : undefined;
}

export function useTaskV2CreateForm({
  open,
  onClose,
  initialData,
}: UseTaskV2CreateFormParams) {
  const nativeUser = useAuthStore((state) => state.nativeUser);
  const {
    users,
    isLoading: isLoadingUsers,
    getDisplayNameById,
  } = useNativeUsersMap();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [responsibleId, setResponsibleId] = useState('');
  const [accompliceIds, setAccompliceIds] = useState<string[]>([]);
  const [auditorIds, setAuditorIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState('');
  /* Напоминание. Срабатывает на бэке: ReminderWorker раз в 30 секунд ищет
   * созревшие remindAt, пишет уведомление ответственному и чистит поле.
   * Локальных таймеров фронту заводить не нужно. */
  const [remindAt, setRemindAt] = useState('');
  const [executionTimeHHMM, setExecutionTimeHHMM] = useState('');
  const [priority, setPriority] = useState<number | null>(null);
  const [taskControl, setTaskControl] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);

  /* Пикер соисполнителей пункта чек-листа оперирует числовыми id (наследие
   * legacy), а у нас UUID. Мост живёт в ref: нумерация должна пережить
   * перерисовку, иначе выбранные люди «переедут». */
  const bridgeRef = useRef(new IdBridge());

  /* Вложения описания и «сохранить как шаблон» — тоже пост-шаги: в теле
   * создания их нет, но есть свои маршруты: POST /files и /save-as-template. */
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  /* Чек-лист и связь с другой задачей набираются локально: задачи ещё нет, а
   * оба маршрута требуют её id. Уходят пост-шагами после создания. */
  const [checklist, setChecklist] =
    useState<FormChecklistValue>(EMPTY_CHECKLIST);
  const [linkedTask, setLinkedTask] = useState<TaskV2Option | null>(null);
  const [crmDeal, setCrmDeal] = useState<CrmDealV2 | null>(null);

  /* Повтор задачи. В v2 правило заводится на уже созданную задачу
   * (POST /v2/tasks/{id}/recurrence), поэтому здесь только форма — запрос уходит
   * после того, как задача создана. */
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);
  const [recurrencePeriod, setRecurrencePeriod] =
    useState<RecurrencePeriodV2>('day');
  const [recurrenceFirstAt, setRecurrenceFirstAt] = useState('');
  const [recurrenceEvery, setRecurrenceEvery] = useState(1);
  const [recurrenceUntil, setRecurrenceUntil] = useState('');

  const hasInitialDeadline = Boolean(initialData?.DEADLINE);

  // Модалка не размонтируется при закрытии, поэтому состояние сбрасываем на
  // каждом открытии — иначе в следующий раз откроется прошлый черновик.
  useEffect(() => {
    if (!open) {
      return;
    }
    setTitle(initialData?.TITLE ?? '');
    setDescription(initialData?.DESCRIPTION ?? '');
    setResponsibleId(nativeUser?.id ?? '');
    setAccompliceIds([]);
    setAuditorIds([]);
    setDeadline(initialData?.DEADLINE ?? '');
    setExecutionTimeHHMM(hoursToHHMM(initialData?.TIME_ESTIMATE_HOURS));
    setPriority(null);
    setTaskControl(false);
    setTitleError(null);
    setChecklist(EMPTY_CHECKLIST);
    setLinkedTask(null);
  }, [
    open,
    nativeUser?.id,
    initialData?.DEADLINE,
    initialData?.TIME_ESTIMATE_HOURS,
    initialData?.TITLE,
    initialData?.DESCRIPTION,
  ]);

  const people = useMemo<PersonItem<string>[]>(
    () =>
      users.map((user) => ({
        id: user.id,
        name: user.displayName || user.email || user.id,
        avatarUrl: null,
      })),
    [users]
  );

  const queryClient = useQueryClient();

  /**
   * Создание идёт в несколько шагов: сама задача, затем команда, чек-лист,
   * связь с другой задачей и правило повторения — у каждого свой маршрут.
   * Транзакции на несколько запросов бэк не даёт, поэтому сбой любого пост-шага
   * не откатывает задачу и не роняет весь submit: о нём честно сообщается
   * отдельным тостом.
   */
  const create = useMutation({
    mutationFn: async (payload: {
      write: TaskV2Write;
      team: { userId: string; role: TaskV2AssigneeRole }[];
      recurrence?: ReturnType<typeof buildRecurrencePayload>;
      checklist?: FormChecklistValue;
      linkedTaskId?: string;
      dealId?: string;
      files?: File[];
      saveAsTemplate?: boolean;
      uuidOf: (n: number) => string | undefined;
    }) => {
      const task = await createTaskV2(payload.write);
      const taskId = task.id;
      const warnings: string[] = [];

      if (taskId) {
        const failed: string[] = [];
        for (const member of payload.team) {
          try {
            await assignTaskUserV2(taskId, member.userId, member.role);
          } catch {
            failed.push(getDisplayNameById(member.userId) || member.userId);
          }
        }
        if (failed.length) {
          warnings.push(`не удалось добавить: ${failed.join(', ')}`);
        }

        if (payload.checklist?.checklists.length) {
          try {
            await pushChecklistV2(taskId, payload.checklist, payload.uuidOf);
          } catch {
            warnings.push('чек-лист сохранён не полностью');
          }
        }

        if (payload.linkedTaskId) {
          try {
            await createTaskRelationV2(taskId, {
              relatedTaskId: payload.linkedTaskId,
            });
          } catch {
            warnings.push('связь с другой задачей не сохранилась');
          }
        }

        for (const file of payload.files ?? []) {
          try {
            await uploadTaskFileV2(taskId, file);
          } catch {
            warnings.push(`файл «${file.name}» не загрузился`);
          }
        }

        if (payload.saveAsTemplate) {
          try {
            await saveTaskAsTemplateV2(taskId, { name: payload.write.title });
          } catch {
            warnings.push('шаблон не сохранился');
          }
        }

        if (payload.dealId) {
          try {
            await linkTaskToDealV2(taskId, payload.dealId);
          } catch {
            warnings.push('привязка к сделке не сохранилась');
          }
        }

        if (payload.recurrence) {
          try {
            await createTaskRecurrenceV2(taskId, payload.recurrence);
          } catch {
            warnings.push('правило повторения не сохранилось');
          }
        }
      }

      return { task, warnings };
    },
    onSuccess: ({ warnings }) => {
      void queryClient.invalidateQueries({ queryKey: tasksV2Keys.all });

      if (warnings.length) {
        toast.warning(`Задача создана, но ${warnings.join('; ')}`);
      } else {
        toast.success('Задача создана');
      }
      onClose();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Не удалось создать задачу'));
    },
  });

  const submit = useCallback(
    (e?: React.BaseSyntheticEvent) => {
      e?.preventDefault();

      if (!title.trim()) {
        setTitleError('Укажите название задачи');
        return;
      }
      setTitleError(null);

      create.mutate({
        write: {
          title: title.trim(),
          description: description.trim() || undefined,
          responsibleId,
          // Поле идёт в Go как time.Time, ему нужен RFC3339. Пустую строку
          // слать нельзя: бэк отвечает `parsing time "" as "T"` — поэтому
          // и на нераспознанном вводе ключ просто не отправляем.
          deadline: toRfc3339(deadline) || undefined,
          remindAt: toRfc3339(remindAt) || undefined,
          timeEstimateSeconds: hhmmToSeconds(executionTimeHHMM),
          priority: priority ?? undefined,
          taskControl,
        },
        team: [
          ...accompliceIds.map((userId) => ({
            userId,
            role: 'accomplice' as const,
          })),
          ...auditorIds.map((userId) => ({
            userId,
            role: 'auditor' as const,
          })),
        ],
        checklist,
        linkedTaskId: linkedTask?.id,
        dealId: crmDeal?.id ? String(crmDeal.id) : undefined,
        files: attachedFiles,
        saveAsTemplate,
        uuidOf: (n: number) => bridgeRef.current.uuid(n),
        /* Правило повторения уходит только если блок раскрыт и первый запуск
         * указан: `startAt` в контракте обязателен, без него запрос смысла не
         * имеет. buildRecurrencePayload вернёт null — ключ просто не поедет. */
        recurrence: recurrenceOpen
          ? buildRecurrencePayload({
              period: recurrencePeriod,
              firstAt: recurrenceFirstAt,
              everyCount: recurrenceEvery,
              untilAt: recurrenceUntil,
              responsibleId: responsibleId || undefined,
            })
          : undefined,
      });
    },
    [
      create,
      title,
      description,
      responsibleId,
      deadline,
      executionTimeHHMM,
      priority,
      taskControl,
      accompliceIds,
      auditorIds,
      checklist,
      linkedTask,
      recurrenceOpen,
      crmDeal,
      recurrencePeriod,
      recurrenceFirstAt,
      recurrenceEvery,
      recurrenceUntil,
    ]
  );

  /**
   * Подставляет поля шаблона в форму. Пустые поля шаблона не затирают уже
   * введённое: пользователь мог что-то заполнить до выбора шаблона.
   */
  const applyTemplate = useCallback((template: TaskTemplateV2) => {
    const p = template.payload;
    if (!p) {
      return;
    }
    if (p.title) {
      setTitle(p.title);
    }
    if (p.description) {
      setDescription(p.description);
    }
    if (p.responsibleId) {
      setResponsibleId(p.responsibleId);
    }
    if (p.accomplices?.length) {
      setAccompliceIds(p.accomplices);
    }
    if (p.auditors?.length) {
      setAuditorIds(p.auditors);
    }
    if (p.timeEstimateSeconds) {
      const total = Math.max(0, Math.round(p.timeEstimateSeconds / 60));
      const hh = String(Math.floor(total / 60)).padStart(2, '0');
      const mm = String(total % 60).padStart(2, '0');
      setExecutionTimeHHMM(`${hh}:${mm}`);
    }
    if (typeof p.priority === 'number') {
      setPriority(clampPriority(p.priority));
    }
    if (typeof p.taskControl === 'boolean') {
      setTaskControl(p.taskControl);
    }
    /* deadlineAfterSeconds — срок ОТНОСИТЕЛЬНО момента создания. Поле формы
     * ждёт абсолютное локальное время в формате datetime-local. */
    if (p.deadlineAfterSeconds) {
      const at = new Date(Date.now() + p.deadlineAfterSeconds * 1000);
      const pad = (n: number) => String(n).padStart(2, '0');
      setDeadline(
        `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`
      );
    }
  }, []);

  return {
    // шаблон и повтор
    applyTemplate,
    recurrenceOpen,
    setRecurrenceOpen,
    recurrencePeriod,
    setRecurrencePeriod,
    recurrenceFirstAt,
    setRecurrenceFirstAt,
    recurrenceEvery,
    setRecurrenceEvery,
    recurrenceUntil,
    setRecurrenceUntil,

    // значения
    title,
    setTitle,
    description,
    setDescription,
    responsibleId,
    setResponsibleId,
    accompliceIds,
    setAccompliceIds,
    auditorIds,
    setAuditorIds,
    checklist,
    setChecklist,
    linkedTask,
    setLinkedTask,
    crmDeal,
    setCrmDeal,
    attachedFiles,
    setAttachedFiles,
    fileInputRef,
    saveAsTemplate,
    setSaveAsTemplate,
    deadline,
    setDeadline,
    hasInitialDeadline,
    remindAt,
    setRemindAt,
    executionTimeHHMM,
    setExecutionTimeHHMM,
    priority,
    setPriority: (value: number | null) =>
      setPriority(value === null ? null : clampPriority(value)),
    taskControl,
    setTaskControl,

    // справочник людей
    people,
    /** Те же люди, но с числовыми id — для пикера соисполнителей пункта. */
    checklistPeople: people.map((p) => ({
      id: bridgeRef.current.num(p.id),
      name: p.name,
    })),
    isLoadingUsers,
    /** Автор задачи. В v2 он берётся из сессии — сменить его нельзя. */
    creator: nativeUser,
    getDisplayNameById,

    // отправка
    submit,
    isSubmitting: create.isPending,
    titleError,
  };
}

export type TaskV2CreateFormApi = ReturnType<typeof useTaskV2CreateForm>;
